/*
 * Copyright 2026 Sony Corporation
 */

import { Alert, Box, Chip, CircularProgress, Snackbar } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import { WorldHeader } from '../components/WorldHeader';
import { ActivityBar } from '../components/panels/ActivityBar';
import { BabylonCanvas } from '../components/scene/BabylonCanvas';
import { ChatPanel } from '../components/chat/ChatPanel';
import { LibraryPanel, type LibraryTab, type PanelMode } from '../components/library/LibraryPanel';
import { SCENE_STUB_PROMPTS } from '../services/agent-stub';
import { KnownDistance } from '../components/scene-page/KnownDistance';
import { ViewportActions } from '../components/scene-page/ViewportActions';
import { ASSET_MIME, unpackAsset } from '../services/asset-drag';
import { digestScene } from '../scene-doc/digest';
import { useSceneAgent } from '../hooks/use-scene-agent';
import { documentStore, isStoredSceneId } from '../services/document-store';
import { downloadBabylon, publishBabylon } from '../scene-doc/serialize';
import { libraryResolver } from '../scene-doc/load-library';
import { useAuth } from '../contexts/auth-context';
import { useDocScene } from '../hooks/use-doc-scene';
import { useSceneDoc } from '../hooks/use-scene-doc';
import { captureThumbnail } from '../scene/thumbnail';
import type { GizmoMode } from '../scene/node-gizmo';

/** Blender's, and near enough every other editor's, so muscle memory transfers. */
const SHORTCUTS: Record<string, GizmoMode> = { q: 'select', w: 'move', e: 'rotate', r: 'scale' };

/** The known-distance tool at work: which asset, and the points clicked so far. */
interface Measuring {
  nodeId: string;
  assetId: string;
  /** Where each click landed, for the markers. */
  world: Vector3[];
  /** The same points in the asset's own units, taken at the click, so moving the node meanwhile changes nothing. */
  local: Vector3[];
}

const MIN_SCALE = 1e-4;
const MAX_SCALE = 1e4;

/**
 * The Scene page: build the 3D environment.
 *
 * Three columns — the library that supplies things, the viewport that arranges
 * them, the chat that does both by conversation (devplan §5.2).
 *
 * U1 makes the middle column real. The document is the truth and the viewport
 * is an output of it: a gizmo drag emits the same `set_transform` a panel would
 * (devplan §3.2), which is why the AI can be added later without a second write
 * path.
 */
export const ScenePage = () => {
  const { world_id: worldId = '', scene_id: sceneId = 'draft' } = useParams();
  const { api } = useAuth();
  const resolveLibrary = useMemo(() => libraryResolver(api), [api]);

  const worldHref = `/worlds/${worldId}`;

  // Stable for the life of the page: the hook saves through it on a timer, and
  // a new identity every render would reset that timer forever.
  const store = useMemo(() => documentStore(worldId, sceneId, api), [worldId, sceneId, api]);
  const {
    doc,
    selection,
    select,
    dispatch,
    dispatchContinuous,
    undo,
    redo,
    canUndo,
    canRedo,
    errors,
    loaded,
    savedAt,
    saveError,
  } = useSceneDoc(store);

  const [mode, setMode] = useState<GizmoMode>('move');
  const [panelMode, setPanelMode] = useState<PanelMode>('library');
  const [tab, setTab] = useState<LibraryTab>('models');
  // The batch whose complaint has been read. Identity, not a flag: a new batch
  // is a new complaint, and dismissing one must not silence the next.
  const [dismissed, setDismissed] = useState<readonly string[]>([]);
  // Imports in flight, and the last one that failed. A model is a download from
  // a CDN we do not control, so its progress belongs on screen.
  const [importing, setImporting] = useState<readonly string[]>([]);
  const [importError, setImportError] = useState<string>();
  // Publishing: a build, an upload of up to 100 MB and a revision, which is far
  // too long to leave a button looking idle.
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [publishError, setPublishError] = useState<string>();
  const [measuringState, setMeasuring] = useState<Measuring>();
  // Deleted, or undone away, while being measured: the tool simply ends.
  const measuring =
    measuringState && doc.nodes.some((node) => node.id === measuringState.nodeId) ? measuringState : undefined;
  const [rescaling, setRescaling] = useState(false);
  const [measureError, setMeasureError] = useState<string>();

  const engineRef = useRef<AbstractEngine>(null);
  const cameraRef = useRef<Camera>(null);

  const agent = useSceneAgent({ worldId });
  // Built when a turn resolves rather than passed as a value, so the assistant
  // is answered about the document as it stands then.
  const digest = useCallback(() => digestScene(doc, selection), [doc, selection]);

  const onAssetState = useCallback((nodeId: string, state: 'loading' | 'ready' | 'failed', error?: string) => {
    setImporting((current) => (state === 'loading' ? [...current, nodeId] : current.filter((id) => id !== nodeId)));
    if (state === 'failed') {
      setImportError(error);
    }
  }, []);

  const { attach, frame, dropPoint, dropTarget, rescale, showMarkers } = useDocScene({
    doc,
    selection,
    mode,
    onSelect: select,
    onDrag: (op) => dispatchContinuous([op]),
    onAssetState,
    resolveLibrary,
    measure: useMemo(
      () =>
        measuring && measuring.world.length < 2
          ? {
              nodeId: measuring.nodeId,
              onPoint: (world: Vector3, local: Vector3 | undefined) => {
                if (!local) {
                  setMeasureError('The asset has not finished loading.');
                  return;
                }
                setMeasureError(undefined);
                setMeasuring({ ...measuring, world: [...measuring.world, world], local: [...measuring.local, local] });
              },
            }
          : undefined,
      [measuring]
    ),
  });

  const selectedLibraryAsset = useMemo(() => {
    const node = selection.length === 1 ? doc.nodes.find((candidate) => candidate.id === selection[0]) : undefined;
    return node?.kind === 'asset' && node.asset.source === 'library'
      ? { nodeId: node.id, assetId: node.asset.slug }
      : undefined;
  }, [doc, selection]);

  const stopMeasuring = useCallback(() => {
    setMeasuring(undefined);
    setMeasureError(undefined);
  }, []);

  const toggleMeasuring = useCallback(() => {
    if (measuring) {
      stopMeasuring();
    } else if (selectedLibraryAsset) {
      setMeasuring({ ...selectedLibraryAsset, world: [], local: [] });
    }
  }, [measuring, selectedLibraryAsset, stopMeasuring]);

  useEffect(() => {
    showMarkers(measuring?.world ?? []);
  }, [measuring, showMarkers]);

  const applyDistance = useCallback(
    (metres: number) => {
      if (!measuring || measuring.local.length < 2) {
        return;
      }
      const scale = metres / Vector3.Distance(measuring.local[0], measuring.local[1]);
      if (!Number.isFinite(scale) || scale < MIN_SCALE || scale > MAX_SCALE) {
        setMeasureError('Those points are too close together to scale from. Pick two further apart.');
        return;
      }
      setRescaling(true);
      setMeasureError(undefined);
      void (async () => {
        try {
          const { asset, siblings } = await api.updateAsset(measuring.assetId, { scale });
          for (const reached of [asset, ...siblings]) {
            rescale(reached.asset_id, reached.scale ?? 1);
          }
          const also = siblings.length > 0 ? ` and ${siblings.map((sibling) => sibling.name).join(', ')}` : '';
          setNotice(`Scaled ${asset.name}${also} to real size.`);
          stopMeasuring();
        } catch (cause) {
          setMeasureError(cause instanceof Error ? cause.message : 'The scale could not be saved.');
        } finally {
          setRescaling(false);
        }
      })();
    },
    [api, measuring, rescale, stopMeasuring]
  );

  const frameSelection = useCallback(() => frame(selection), [frame, selection]);

  const drop = useCallback(
    (event: React.DragEvent) => {
      const asset = unpackAsset(event.dataTransfer);
      if (!asset) {
        return;
      }
      event.preventDefault();

      if (asset.kind === 'texture') {
        const nodeId = dropTarget(event.clientX, event.clientY);
        if (nodeId) {
          dispatch([{ op: 'set_material', node_ids: [nodeId], slug: asset.slug, pinned_hash: asset.pinnedHash }]);
        }
        return;
      }

      // Absent when the drop was aimed at the sky, which is a miss rather than
      // a reason to refuse: it then lands where a click would have put it.
      const position = dropPoint(event.clientX, event.clientY);
      dispatch([
        {
          op: 'add_asset',
          source: asset.source ?? 'polyhaven',
          slug: asset.slug,
          name: asset.name,
          pinned_hash: asset.pinnedHash,
          ...(position ? { position } : {}),
        },
      ]);
    },
    [dispatch, dropPoint, dropTarget]
  );

  const remove = useCallback(() => {
    if (selection.length > 0) {
      dispatch([{ op: 'delete', node_ids: [...selection] }]);
    }
  }, [dispatch, selection]);

  const duplicate = useCallback(() => {
    if (selection.length > 0) {
      dispatch([{ op: 'duplicate', node_ids: [...selection] }]);
    }
  }, [dispatch, selection]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) {
        return;
      }

      const tool = SHORTCUTS[event.key.toLowerCase()];
      if (event.key === 'Escape' && measuring) {
        stopMeasuring();
      } else if (tool) {
        setMode(tool);
      } else if (event.key.toLowerCase() === 'f') {
        frameSelection();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        remove();
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [remove, undo, redo, frameSelection, measuring, stopMeasuring]);

  /**
   * Save is a build, and for a scene the service owns it ends in a revision the
   * Device page can load. A scratch scene has nowhere to publish to, so it
   * still downloads the file.
   */
  const save = useCallback(() => {
    const engine = engineRef.current;
    if (!engine || publishing) {
      return;
    }
    if (!isStoredSceneId(sceneId)) {
      void downloadBabylon(engine, doc, sceneId, api);
      return;
    }

    setPublishing(true);
    setPublishError(undefined);
    void (async () => {
      try {
        // Before the build, which runs a throwaway scene on this same engine:
        // the thumbnail is meant to be the view the author pressed Save on.
        const thumbnail = cameraRef.current ? await captureThumbnail(cameraRef.current) : undefined;

        // The document goes first: a published build whose document was never
        // saved is a scene nobody can edit again.
        await store.save(doc);
        const scene = await publishBabylon(engine, doc, worldId, sceneId, api, thumbnail);
        await api.setWorldScene(worldId, { scene_id: sceneId });
        setNotice(`Saved revision ${scene.revision} and opened it in the world.`);
      } catch (cause) {
        setPublishError(cause instanceof Error ? cause.message : 'The scene could not be saved.');
      } finally {
        setPublishing(false);
      }
    })();
  }, [api, doc, publishing, sceneId, store, worldId]);

  return (
    <Box className="flex h-screen flex-col">
      <WorldHeader worldId={worldId} />

      <Box className="flex min-h-0 flex-1">
        {/* The same rail the Device page carries. Without a world in the URL
            its three world-scoped views have nothing to open, so they go and
            ask for one. */}
        <ActivityBar active="scene" linkTo={{ devices: worldHref, deploy: worldHref }} />

        <LibraryPanel
          doc={doc}
          selection={selection}
          mode={panelMode}
          onModeChange={setPanelMode}
          tab={tab}
          onTabChange={setTab}
          onSelect={select}
          dispatch={dispatch}
        />

        <Box
          className="relative flex min-w-0 flex-1"
          onDragOver={(event) => {
            if (event.dataTransfer.types.includes(ASSET_MIME)) {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
            }
          }}
          onDrop={drop}
        >
          {/* No rigs: a scene is scenery, and this screen must not be able to
              move a device (devplan §4). */}
          <BabylonCanvas
            className="min-w-0 flex-1 outline-none"
            withRigs={false}
            onReady={(context) => {
              engineRef.current = context.editor.engine;
              cameraRef.current = context.editor.camera;
              return attach(context);
            }}
          />

          {/* Over the render, because that is what they count. Not interactive,
              so they must never intercept a click meant for the scene. */}
          <Box className="pointer-events-none absolute top-3 left-3 flex gap-2">
            <Chip label={`${doc.nodes.length} objects`} size="small" variant="outlined" />
            {importing.length > 0 && (
              <Chip
                icon={<CircularProgress size={12} />}
                label={`Loading ${importing.length}`}
                size="small"
                variant="outlined"
              />
            )}
            {publishing ? (
              <Chip icon={<CircularProgress size={12} />} label="Saving" size="small" variant="outlined" />
            ) : !loaded ? (
              <Chip icon={<CircularProgress size={12} />} label="Opening" size="small" variant="outlined" />
            ) : (
              savedAt && (
                <Chip
                  label={`Saved ${savedAt.toLocaleTimeString()}`}
                  size="small"
                  variant="outlined"
                  sx={{ color: 'text.secondary' }}
                />
              )
            )}
          </Box>

          <ViewportActions
            mode={mode}
            onModeChange={setMode}
            hasSelection={selection.length > 0}
            onDuplicate={duplicate}
            onDelete={remove}
            onFrame={frameSelection}
            canMeasure={selectedLibraryAsset !== undefined}
            measuring={measuring !== undefined}
            onMeasure={toggleMeasuring}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
            onSave={save}
          />

          {measuring && (
            <KnownDistance
              points={measuring.world.length}
              measured={
                measuring.world.length === 2 ? Vector3.Distance(measuring.world[0], measuring.world[1]) : undefined
              }
              busy={rescaling}
              error={measureError}
              onApply={applyDistance}
              onRestart={() => setMeasuring({ ...measuring, world: [], local: [] })}
              onCancel={stopMeasuring}
            />
          )}

          <ChatPanel
            digest={digest}
            apply={dispatch}
            requestOps={agent.requestOps}
            suggestions={SCENE_STUB_PROMPTS}
            placeholder="Describe the scene you want"
            onNewThread={agent.newThread}
            streaming={agent.streaming}
          />
        </Box>
      </Box>

      <Snackbar
        open={importError !== undefined}
        autoHideDuration={8000}
        onClose={() => setImportError(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="error" onClose={() => setImportError(undefined)}>
          {importError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={errors.length > 0 && errors !== dismissed}
        autoHideDuration={6000}
        onClose={() => setDismissed(errors)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="warning" onClose={() => setDismissed(errors)}>
          {errors[0]}
        </Alert>
      </Snackbar>

      {/* Saving is the one action with a cost, so its outcome is reported
          either way rather than only when it fails. */}
      <Snackbar
        open={notice !== undefined}
        autoHideDuration={6000}
        onClose={() => setNotice(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      </Snackbar>

      <Snackbar
        open={publishError !== undefined || saveError !== undefined}
        autoHideDuration={10000}
        onClose={() => setPublishError(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="error" onClose={() => setPublishError(undefined)}>
          {publishError ?? saveError?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
